import { Type } from '@angular/core';

import { UIRouter } from '@uirouter/core';
import { Category, Ng2StateDeclaration, Transition } from '@uirouter/angular';
import { StickyStatesPlugin } from '@uirouter/sticky-states';

import {
  HelpLinkService,
  Logger,
  Level,
  RouterConfigService,
  staticImplements,
  InitialStateService
} from '@tdwb/core';

import {
  RouterIntegrationService,
  RouterModalService,
  RouterScrollService,
  RouterSecurityService,
  RouterStatusService,
  RouterSystemErrorService,
  UnsupportedBrowserService,
  RouterParamSerializers
} from './'

export type StatesList = Array<Ng2StateDeclaration>;
export type LazyLoad<T> = Type<T> | Promise<Type<T>>;

@staticImplements<RouterConfigService<AppUIRouterConfig, UIRouter>>()
export class AppUIRouterConfig {
  private static readonly LOGGER: Logger = Logger.getLogger('RouterConfigService').setLevel(Level.ERROR);

  static configure(uiRouter: UIRouter): void {
    // sticky states plugin used by modal framework
    uiRouter.plugin(StickyStatesPlugin);
    AppUIRouterConfig.configureTrace(uiRouter);
    AppUIRouterConfig.disableListenForUrlChange(uiRouter);
    AppUIRouterConfig.configureSerializers(uiRouter);
    AppUIRouterConfig.setCaseInsensitiveUrls(uiRouter);
    AppUIRouterConfig.configureBaseRoutes(uiRouter);
    AppUIRouterConfig.configureErrorHandler(uiRouter);
    AppUIRouterConfig.configureTransitionHooks(uiRouter);
    AppUIRouterConfig.configureHelp(uiRouter);
    AppUIRouterConfig.configureOldRedirects(uiRouter);
  }

  static configureTrace(uiRouter: UIRouter): void {
    const enableTrace = window.location.href.match(/debug=true/) !== null;
    if (enableTrace) {
      uiRouter.trace.enable(Category.TRANSITION);
      uiRouter.trace.enable(Category.HOOK);
      uiRouter.trace.enable(Category.RESOLVE);
      uiRouter.trace.enable(Category.UIVIEW);
      uiRouter.trace.enable(Category.VIEWCONFIG);
    }
  }

  static configureHelp(uiRouter: UIRouter): void {
    uiRouter.transitionService.onBefore(
      { to: 'page.help' },
      (transition: Transition): boolean => {
        const articleId = transition.params().articleId;
        if (articleId === null) {
          const fromState = transition.from()?.name;
          if (fromState === '') {
            transition.router.urlService.url('/#');
          }
        }
        const helpService = transition.injector().get(HelpLinkService);
        helpService.open(articleId);
        return false;
      });
  }

  static configureSerializers(uiRouter: UIRouter): void {
    // add custom serializers
    const serializers = RouterParamSerializers.getSerializers();
    for (const serializer of Object.keys(serializers)) {
      uiRouter.urlService.config.type(serializer, serializers[serializer]);
    }
  }

  static configureBaseRoutes(uiRouter: UIRouter): void {
    // register base routes
    AppUIRouterConfig.getBaseRoutes().forEach((baseRoute: Ng2StateDeclaration): void => {
      uiRouter.stateRegistry.register(baseRoute);
    });
  }

  static configureErrorHandler(uiRouter: UIRouter): void {
    // attach error handler.
    uiRouter.stateService.defaultErrorHandler((error: string): void => {
      // setTimeout is here because
      // ui router incorrectly handles promise rejection in route resolves which breaks change detection
      // and can cause angular zone to never stabilize creating script timeouts in  e2e tests which test such conditions
      setTimeout((): void => AppUIRouterConfig.LOGGER.error(error), 0);
    });
  }

  static configureTransitionHooks(uiRouter: UIRouter): void {
    // transition hooks for validations
    RouterSecurityService.initHooks(uiRouter.transitionService);
    RouterModalService.initHooks(uiRouter.transitionService);
    RouterStatusService.initHooks(uiRouter.transitionService);
    RouterSystemErrorService.initHooks(uiRouter.transitionService);
    RouterScrollService.initHooks(uiRouter.transitionService);
    RouterIntegrationService.initHooks(uiRouter.transitionService);
    UnsupportedBrowserService.initHooks(uiRouter.transitionService);
  }

  private static getBaseRoutes(): StatesList {
    return [
      {
        name: 'page', // parent state for all pages
        url: '/page',
        abstract: true,
        sticky: true
      },
      {
        name: 'modal', // parent page for all modals
        url: '/modal',
        abstract: true,
        data: {
          modal: true
        }
      },
      {
        name: 'initial', // alias for initial state.
        url: '',
        redirectTo(transition: Transition): string {
          const initialStateService: InitialStateService = transition.injector().get(InitialStateService);
          const initialState = initialStateService.getInitialState();
          if (initialState !== undefined) {
            return initialState.name.replace('.**', '');
          }
        }
      }
    ];
  }

  private static disableListenForUrlChange(uiRouter: UIRouter): void {
    uiRouter.urlService.deferIntercept();
  }

  private static setCaseInsensitiveUrls(uiRouter: UIRouter): void {
    uiRouter.urlService.config.caseInsensitive(true);
  }

  private static configureOldRedirects(uiRouter: UIRouter): void {
    uiRouter.urlService.rules.when('/modal/accounts/transfer', '/modal/transfers/cash');
    uiRouter.urlService.rules.when('/modal/accounts/securities-transfers', '/modal/transfers/securities');
  }
}
